/**
 * shared/pulse/src/sandbox/index.ts — Week 1 public entry point.
 *
 * What you get when you import the sandbox:
 *
 *   import { Pulse } from "@zeq/pulse/sandbox";
 *   Pulse.init({ debug: true });
 *   await Pulse.zone.write("hero", "<h1>Welcome</h1>");
 *
 * This file deliberately exposes ONLY what the agent needs. Internal
 * helpers (resolveZone, isUnderLocked, sanitiser internals) stay
 * package-private.
 */

import { ZeqZone } from "./zone-api.js";
import { configureRejectionLog, drainRejectionQueue } from "./rejection-log.js";
import { mountOrb } from "../orb/orb-element.js";
import { ZeqContract } from "../contracts/zeq-contract.js";

export interface PulseInitConfig {
  /** Where to POST blocked-write events. Default: "/api/chain/rejection-log". */
  rejectionLogEndpoint?: string;
  /** Toggle the visual outline + permission badges. Default: false. */
  debug?: boolean;
  /** Override the auto-injected containment.css href. Default: "/pulse/containment.css". */
  containmentStylesheetHref?: string | false;
  /**
   * Whether to mount the visible orb on init. Default: true.
   * Set to false on pages that already have a custom mount point or
   * on test harnesses that want a headless Pulse.
   */
  mountOrb?: boolean;
  /**
   * Inline CSS for the orb. The Phase-4 bundle inlines orb.css here so
   * the orb works without an extra stylesheet round-trip. If null, the
   * caller must <link rel="stylesheet" href="/pulse/orb/orb.css">.
   * Default: null.
   */
  orbCss?: string | null;
}

let initialised = false;

/**
 * Initialise the Pulse sandbox on the current document. Idempotent.
 * Must be called from page code (NOT the agent) — agents cannot call init.
 */
function init(cfg: PulseInitConfig = {}): void {
  if (initialised) return;
  initialised = true;

  configureRejectionLog({
    endpoint: cfg.rejectionLogEndpoint ?? "/api/chain/rejection-log",
    consoleWarn: !!cfg.debug,
    maxQueueSize: 256,
  });

  // Inject the containment stylesheet unless the page explicitly opts out
  // by setting cfg.containmentStylesheetHref to false. This is the third
  // sandbox layer — without it the zone API still validates writes, but
  // the runtime CSS isolation is missing.
  const href = cfg.containmentStylesheetHref;
  if (href !== false && !document.querySelector('link[data-zeq-pulse-containment]')) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = typeof href === "string" ? href : "/pulse/containment.css";
    link.setAttribute("data-zeq-pulse-containment", "");
    // Mark as locked so the agent can't remove it.
    link.setAttribute("data-zeq-locked", "");
    document.head.appendChild(link);
  }

  if (cfg.debug) {
    document.documentElement.classList.add("pulse-debug");
  }

  // Drain anything queued by previous sessions (Off-Pulse rejection log).
  void drainRejectionQueue();

  // Phase 2 — mount the visible orb. Default-on; opt-out for tests.
  if (cfg.mountOrb !== false) {
    try {
      mountOrb(cfg.orbCss ?? null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[pulse:init] orb mount failed", err);
    }
  }

  // The 7-step wizard is the homepage itself (apps/zeq-dev/public/index.html
  // sections 1–7 with real /api/zeq-auth/* auth inline). The Pulse panel
  // does NOT auto-launch a takeover wizard on any page — the orb stays
  // available everywhere; the wizard stays on the home.

  // Surface a window-global so non-module page scripts can call the API.
  (window as any).Pulse = Pulse;
}

/**
 * Public namespace. Identical surface whether you import as a module or
 * use the global injected by init().
 *
 *   Pulse.zone.write(...)           — Phase 1 sandboxed DOM API
 *   Pulse.contract.bind(...)        — Phase 3 contract execution
 *   Pulse.init(cfg)                 — boot the runtime
 */
export const Pulse = {
  init,
  zone: ZeqZone,
  contract: ZeqContract,
  /** Re-exposed for tests + the page integration tutorial in templates/page.html. */
  drainRejectionQueue,
};

export { ZeqZone } from "./zone-api.js";
export type { ZoneResult, LayoutInstruction, ComponentDef } from "./zone-api.js";
export type { RejectionReason, RejectionEntry } from "./rejection-log.js";
export { ZeqContract } from "../contracts/zeq-contract.js";
export type { ContractBindArgs, ContractResult, ContractExecuteAt } from "../contracts/zeq-contract.js";

export default Pulse;

// ─── Auto-boot ──────────────────────────────────────────────────────────
//
// When pulse.js is loaded as a classic <script src="/pulse.js" data-zid="...">,
// we read the script tag's data attributes and call Pulse.init() automatically.
// This lets the slug-host injection point use ONE tag without a separate
// boot snippet, and keeps the public API clean.
//
// Honoured attributes:
//   data-zid                          — bind to a state machine (required).
//   data-pulse-rejection-endpoint     — override audit log endpoint.
//   data-pulse-debug                  — set "1" to outline zones / log to console.
//   data-pulse-orb                    — set "0" to disable the orb (sandbox-only).
//
// ─── Phase CJ.5 — universal orb-tap router (capture phase) ──────────────
// The orb (#zeq-pulse-orb) is mounted by several code paths across the
// framework (this bundle's autoBoot, the workbench suite on /s/ machine
// pages, pulse-everywhere on node homepages). To guarantee ONE tap
// behaviour everywhere — open the framework AI entry, never the retired
// inline workbench — we install a single document-level capture-phase
// click listener. Capture fires before any bubble-phase handler the other
// bundles attached, and stopImmediatePropagation prevents them running.
// Destination follows the machine's recorded choice in localStorage
// "zeq_pick_mode": "cli" -> the built-in Root CLI app, else Pulse (AI).
(function installOrbRouter() {
  if (typeof document === "undefined") return;
  if ((window as any).__zeqOrbRouteInstalled) return;
  (window as any).__zeqOrbRouteInstalled = true;
  const route = (ev: Event): void => {
    const t = ev.target as Element | null;
    const orb = t && (t as any).closest ? t.closest("#zeq-pulse-orb") : null;
    if (!orb) return;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    let mode = "ai";
    try { mode = (localStorage.getItem("zeq_pick_mode") || "ai").toLowerCase(); } catch { /* storage blocked */ }
    const dest = mode === "cli" ? "https://zeq.me/apps/cli/" : "https://ai.zeq.me/";
    window.open(dest, "_blank", "noopener,noreferrer");
  };
  document.addEventListener("click", route, true);
  document.addEventListener("keydown", (ev: KeyboardEvent) => {
    if (ev.key !== "Enter" && ev.key !== " " && ev.key !== "Spacebar") return;
    const t = ev.target as Element | null;
    if (t && (t as any).closest && t.closest("#zeq-pulse-orb")) route(ev);
  }, true);
})();

// document.currentScript is null inside ESM modules, but this bundle ships
// as IIFE so it resolves to the live <script> element during evaluation.
(function autoBoot() {
  if (typeof document === "undefined") return;
  const tag = document.currentScript as HTMLScriptElement | null;
  if (!tag) return;
  const zid = tag.dataset.zid;
  if (!zid) return;
  if ((window as any).__zeqPulseAutoBooted) return;
  (window as any).__zeqPulseAutoBooted = true;

  const cfg: PulseInitConfig = {
    rejectionLogEndpoint: tag.dataset.pulseRejectionEndpoint || "/api/chain/rejection-log",
    debug: tag.dataset.pulseDebug === "1",
    mountOrb: tag.dataset.pulseOrb !== "0",
  };

  const run = () => {
    try { init(cfg); } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[pulse:auto-boot] init failed", err);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  } else {
    run();
  }
})();
